/**
 * PlatformLayoutRoute — wraps every /platform/* page (and the platform
 * chat-log at /chat-log) and enforces admin.system. Tenant-side users
 * (TPO admins, faculty) are redirected to the home page.
 *
 * White-label PARTNER logins (admin.org / moderator.manage carrying a
 * partnerId) also pass — they reuse the platform Colleges page, scoped to
 * their own colleges by the API. Their rail only exposes Colleges; the
 * platform-only pages they can't usefully reach are gated server-side.
 *
 * SUPPORT agents (moderator.approve) pass too, but are confined to the ticket
 * queue: any other path under this layout — including /chat-log — bounces
 * them to /platform/support. This is the display half of the rule; the API
 * half is SUPPORT_DESK in platform.shared.ts, which is what actually keeps
 * them out of the other platform endpoints.
 *
 * Each child page still renders its own PlatformTopBar — this layout
 * only owns the role gate + the page background.
 */

import { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth';
import { hasRole } from '../../../shared/utils';

/** The only path prefix a support agent may reach under this layout. */
export const SUPPORT_HOME = '/platform/support';

export function PlatformLayoutRoute() {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" replace />;
  }

  const isPartner = !!(user as any)?.partnerId;
  const isSupport = hasRole(user, ['moderator.approve']);

  if (!hasRole(user, ['admin.system']) && !isPartner && !isSupport) {
    return <Navigate to="/" replace />;
  }

  // Support agents see the ticket queue and nothing else under /platform.
  // Paper reviews is the one other /platform surface a support agent owns:
  // they approve colleagues' papers there (never their own).
  const SUPPORT_REVIEWS = '/platform/paper-reviews';
  if (isSupport && !hasRole(user, ['admin.system'])
      && pathname !== SUPPORT_HOME && !pathname.startsWith(`${SUPPORT_HOME}/`)
      && pathname !== SUPPORT_REVIEWS) {
    return <Navigate to={SUPPORT_HOME} replace />;
  }

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>}>
      <Outlet />
    </Suspense>
  );
}
